import React, { useState, useEffect } from 'react';
import './InstallPwaModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faTimes,
    faStar,
    faCheckCircle,
    faLaptop,
    faMobileAlt
} from '@fortawesome/free-solid-svg-icons';

export default function InstallPwaModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [detectedOS, setDetectedOS] = useState('android'); // 'android' | 'ios' | 'mac' | 'pc'
    const [isInstalled, setIsInstalled] = useState(false);
    const [showSteps, setShowSteps] = useState(false);

    useEffect(() => {
        // Detectar si ya está corriendo como PWA instalada
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true ||
            document.referrer.includes('android-app://');

        if (isStandalone) {
            setIsInstalled(true);
            return;
        }

        // Detectar SO del usuario de forma precisa
        const ua = navigator.userAgent || navigator.vendor || window.opera || '';
        const isIOS = (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream;
        const isAndroid = /Android/i.test(ua);
        const isMac = (/Macintosh|Mac OS X/.test(ua) || navigator.platform?.toUpperCase().indexOf('MAC') >= 0) && !isIOS;

        let os = 'pc';
        if (isAndroid) {
            os = 'android';
        } else if (isIOS) {
            os = 'ios';
        } else if (isMac) {
            os = 'mac';
        } else {
            os = 'pc';
        }
        setDetectedOS(os);

        // Escuchar evento nativo de instalación en Chromium (Android, Chrome, Edge)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            window.deferredPwaPrompt = e;
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Si ya había sido capturado previamente en window
        if (window.deferredPwaPrompt) {
            setDeferredPrompt(window.deferredPwaPrompt);
        }

        // Escuchar evento personalizado para reabrir el modal desde navbar o botón
        const handleOpenCustom = () => {
            if (window.deferredPwaPrompt) {
                setDeferredPrompt(window.deferredPwaPrompt);
            }
            setIsInstalled(false);
            setShowSteps(false);
            setIsOpen(true);
        };
        window.addEventListener('open-pwa-install', handleOpenCustom);

        // Abrir automáticamente al ingresar si no ha sido cerrado en esta sesión
        const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
        if (!isDismissed) {
            const timer = setTimeout(() => {
                setIsOpen(true);
            }, 2000);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
                window.removeEventListener('open-pwa-install', handleOpenCustom);
            };
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('open-pwa-install', handleOpenCustom);
        };
    }, []);

    const handleInstallClick = async () => {
        const promptEvent = deferredPrompt || window.deferredPwaPrompt;
        if (promptEvent) {
            try {
                promptEvent.prompt();
                const choiceResult = await promptEvent.userChoice;
                if (choiceResult && choiceResult.outcome === 'accepted') {
                    setIsOpen(false);
                }
                setDeferredPrompt(null);
                window.deferredPwaPrompt = null;
                return;
            } catch (err) {
                console.warn('Error al invocar prompt:', err);
            }
        }
        // Si el navegador no soporta el prompt automático directo, mostramos los pasos guiados
        setShowSteps(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        sessionStorage.setItem('pwa_prompt_dismissed', 'true');
    };

    if (!isOpen || isInstalled) {
        return null;
    }

    return (
        <div className="pwaModalOverlay" onClick={handleClose}>
            <div className="pwaModalContainer" onClick={(e) => e.stopPropagation()}>
                {/* Botón cerrar */}
                <button
                    type="button"
                    className="pwaBtnClose"
                    onClick={handleClose}
                    aria-label="Cerrar"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                {/* Header de la Aplicación */}
                <div className="pwaAppHeader">
                    <img
                        src="/logo192.png"
                        alt="Logo Club Vende Más"
                        className="pwaAppIcon"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo.png';
                        }}
                    />
                    <div className="pwaAppHeaderInfo">
                        <div className="pwaAppBadgeVerified">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span>App Oficial</span>
                        </div>
                        <h3 className="pwaAppTitle">Club Vende Más</h3>
                        <p className="pwaAppSubtitle">Plataforma Líder de Comercio</p>
                        <div className="pwaAppRating">
                            <span className="pwaStars">
                                {[...Array(5)].map((_, i) => (
                                    <FontAwesomeIcon key={i} icon={faStar} />
                                ))}
                            </span>
                            <span className="pwaRatingText">4.9 • Gratis</span>
                        </div>
                    </div>
                </div>

                {/* Contenido enfocado únicamente en el Sistema Operativo detectado */}
                <div className="pwaPlatformContent">
                    {/* CASO ANDROID */}
                    {detectedOS === 'android' && (
                        <div className="pwaTabPanel">
                            <p className="pwaPanelDescription">
                                Instala la app en tu <strong>Android</strong> para navegar más rápido y acceder sin escribir la web.
                            </p>

                            {!showSteps ? (
                                <button
                                    type="button"
                                    className="pwaPrimaryCtaBtn btnAndroid"
                                    onClick={handleInstallClick}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    <span>Instalar App en Android</span>
                                </button>
                            ) : (
                                <div className="pwaStepsBox">
                                    <h4 className="pwaStepsBoxTitle">Pasos para instalar en Android:</h4>
                                    <ol className="pwaStepsList">
                                        <li>
                                            <span className="stepNum">1</span>
                                            <span>Toca el menú de <strong>tres puntos (⋮)</strong> en la esquina superior de Chrome.</span>
                                        </li>
                                        <li>
                                            <span className="stepNum">2</span>
                                            <span>Selecciona <strong>"Instalar aplicación"</strong> o <strong>"Agregar a pantalla principal"</strong>.</span>
                                        </li>
                                        <li>
                                            <span className="stepNum">3</span>
                                            <span>Presiona <strong>"Instalar"</strong>.</span>
                                        </li>
                                    </ol>
                                    <button
                                        type="button"
                                        className="pwaPrimaryCtaBtn btnAndroid"
                                        onClick={handleClose}
                                        style={{ marginTop: '0.85rem' }}
                                    >
                                        <span>Entendido</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* CASO IPHONE / IPAD */}
                    {detectedOS === 'ios' && (
                        <div className="pwaTabPanel">
                            <p className="pwaPanelDescription">
                                Agrégala a la pantalla de inicio de tu <strong>iPhone / iPad</strong> desde Safari:
                            </p>

                            <div className="pwaStepsBox">
                                <ol className="pwaStepsList">
                                    <li>
                                        <span className="stepNum">1</span>
                                        <span>Toca el botón <strong>Compartir</strong>
                                            <span className="appleShareIconBadge" title="Icono compartir">
                                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                                    <polyline points="16 6 12 2 8 6" />
                                                    <line x1="12" y1="2" x2="12" y2="15" />
                                                </svg>
                                            </span>
                                            en la barra inferior de Safari.
                                        </span>
                                    </li>
                                    <li>
                                        <span className="stepNum">2</span>
                                        <span>Selecciona <strong>"Agregar al inicio"</strong> ➕.</span>
                                    </li>
                                    <li>
                                        <span className="stepNum">3</span>
                                        <span>Toca <strong>"Agregar"</strong> arriba a la derecha.</span>
                                    </li>
                                </ol>
                            </div>

                            <button
                                type="button"
                                className="pwaPrimaryCtaBtn btnIos"
                                onClick={handleClose}
                            >
                                <span>Entendido</span>
                            </button>
                        </div>
                    )}

                    {/* CASO MAC */}
                    {detectedOS === 'mac' && (
                        <div className="pwaTabPanel">
                            <p className="pwaPanelDescription">
                                Instala en tu <strong>Mac</strong> para abrirla con un clic desde el <strong>Dock</strong>.
                            </p>

                            {(!showSteps && deferredPrompt) ? (
                                <button
                                    type="button"
                                    className="pwaPrimaryCtaBtn btnMac"
                                    onClick={handleInstallClick}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    <span>Instalar App en Mac</span>
                                </button>
                            ) : (
                                <div className="pwaStepsBox macStepsBox">
                                    <h4 className="pwaStepsBoxTitle">Cómo instalar en Mac:</h4>
                                    <div className="pwaMacOptions">
                                        <div className="pwaMacOptionItem">
                                            <span className="pwaMacBadge">Safari (macOS):</span>
                                            <p>Haz clic en <strong>Archivo</strong> &gt; <strong>"Agregar al Dock..."</strong>.</p>
                                        </div>
                                        <div className="pwaMacOptionItem">
                                            <span className="pwaMacBadge">Chrome en Mac:</span>
                                            <p>Haz clic en el icono <strong>(⬇)</strong> en la barra de direcciones o Menú &gt; <strong>Instalar</strong>.</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="pwaPrimaryCtaBtn btnMac"
                                        onClick={handleClose}
                                        style={{ marginTop: '0.85rem' }}
                                    >
                                        <span>Entendido</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* CASO WINDOWS / PC */}
                    {detectedOS === 'pc' && (
                        <div className="pwaTabPanel">
                            <p className="pwaPanelDescription">
                                Instala Club Vende Más en tu computadora para una experiencia más rápida a pantalla completa.
                            </p>

                            {!showSteps ? (
                                <button
                                    type="button"
                                    className="pwaPrimaryCtaBtn btnPc"
                                    onClick={handleInstallClick}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    <span>Instalar App en tu PC</span>
                                </button>
                            ) : (
                                <div className="pwaStepsBox">
                                    <ol className="pwaStepsList">
                                        <li>
                                            <span className="stepNum">1</span>
                                            <span>En Chrome o Edge, haz clic en el icono de <strong>Instalar (⬇)</strong> en la barra de direcciones.</span>
                                        </li>
                                        <li>
                                            <span className="stepNum">2</span>
                                            <span>O en el menú <strong>(⋮)</strong> &gt; <strong>"Instalar Club Vende Más"</strong>.</span>
                                        </li>
                                    </ol>
                                    <button
                                        type="button"
                                        className="pwaPrimaryCtaBtn btnPc"
                                        onClick={handleClose}
                                        style={{ marginTop: '0.85rem' }}
                                    >
                                        <span>Entendido</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer del Modal */}
                <div className="pwaModalFooter">
                    <button
                        type="button"
                        className="pwaBtnDismiss"
                        onClick={handleClose}
                    >
                        Ahora no
                    </button>
                </div>
            </div>
        </div>
    );
}
